import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { AuditIssue } from '../../api/types';
import { ColumnDescription, TableComponent } from '../components/table';
import { ServicePortalApi } from '../../api/api';
import {
  SynIconComponent,
  SynOptionComponent,
  SynSelectComponent,
} from '@synergy-design-system/angular';
import { SynChangeEvent, SynSelect } from '@synergy-design-system/components';
import { RouterLink } from '@angular/router';
import { StateService } from '../../api/state';

function toEntries(map: Map<string, number>): [string, number][] {
  const entries = [...map.entries()];
  entries.sort((a, b) => a[0].localeCompare(b[0]));
  return entries;
}

interface IssueDescription {
  name: string;
  link?: string;
  text: string;
}

// We do not translate the descriptions for now, since we expect client admins to be able to understand English
// Our documentation in Mosaic+ is also mostly only available in English
const ISSUE_DESCRIPTIONS: IssueDescription[] = [
  {
    name: 'no-login',
    link: 'https://mosaicplus.sick.com/x/URvhnQ',
    text: `
    No user has logged into your client for a long time (last login: $data).
    If you do not need this client, please remove it.
    `,
  },
  {
    name: 'full-scope-allowed',
    link: 'https://mosaicplus.sick.com/x/Dx3hnQ',
    text: `
    The client has the setting "Fullscope" enabled.
    Since this feature includes all user roles into the access token, it may prevent login for users with many roles.
    You should only map required roles.
    `,
  },
  {
    name: 'public-without-pkce',
    link: 'https://mosaicplus.sick.com/x/Wi3hnQ',
    text: `
    The client is public but does not enforce PKCE.
    To secure your client, we strongly recommend to either enforce PKCE or use a confidential client.
    `,
  },
  {
    name: 'secret-too-old',
    link: 'https://mosaicplus.sick.com/x/8C-hnQ',
    text: `
    The client secret has not been rotated for a long time.
    To secure your client, we strongly recommend to rotate the secret regularly.
    `,
  },
  {
    name: 'client-admin-role-exists',
    link: 'https://mosaicplus.sick.com/x/ijbhnQ',
    text: `
    The old role client_admin still exists in your client.
    This role was used to manage permissions but is no longer necessary.
    You can remove it if your application does not use it.
    `,
  },
  {
    name: 'single-admin',
    link: 'https://mosaicplus.sick.com/x/dzfhnQ',
    text: `
    The client only has a single administrator.
    Add a backup administrator to make access to this client more robust.
    `,
  },
  {
    name: 'redirect-uri-postman',
    link: 'https://mosaicplus.sick.com/x/0TnhnQ',
    text: `
    The client has configured Postman as a valid redirect URL on Prod or UAT.
    Development URLs should only be used on the Test stage to reduce phishing possibilities.
    `,
  },
  {
    name: 'redirect-uri-localhost',
    link: 'https://mosaicplus.sick.com/x/0TnhnQ',
    text: `
    The client has configured localhost as a valid redirect URL on Prod or UAT.
    Localhost URLs are usually used for development.
    Development URLs should only be used on the Test stage to reduce phishing possibilities.
    `,
  },
  {
    name: 'web-origin-localhost',
    link: 'https://mosaicplus.sick.com/x/0TnhnQ',
    text: `
    The client has configured localhost as a web origin on Prod or UAT.
    Localhost URLs are usually used for development.
    Development URLs should only be used on the Test stage to reduce phishing possibilities.
    `,
  },
  {
    name: 'no-admin',
    text: `
    The client has no admin.
    This may happen if the last administrator left SICK and their SICK ID User has been deleted.
    `,
  },
  {
    name: 'unmanaged',
    text: `
    The client is not managed via the service portal.
    `,
  },
  {
    name: 'not-upgraded',
    text: `
    The client has not been upgraded to the new permission model.
    This happens if no client admin has accessed this client since the deployment of the new service portal.
    `,
  },
  {
    name: 'misconfigured-version',
    text: `
    The client version could not be determined.
    A client should be automatically upgraded to the new permission model at first access.
    Something might have gone wrong during the upgrade.
    `,
  },

];

function lookupDescription(reason: string): IssueDescription | null {
  for (const desc of ISSUE_DESCRIPTIONS) {
    if (desc.name === reason) {
      return desc;
    }
  }
  return null;
}

@Component({
  selector: 'app-audit-list',
  template: `
    <h1>SICK ID Guard
      <syn-icon name="shield_moon_fill" />
    </h1>
    <p i18n="@@security-audit-intro">
      Find security and maintenance issues in your client configuration.
    </p>

    <div class="flex-row gap-1 margin-bottom-1">
      <syn-select label="Clients"
                  i18n-label="@@clients"
                  size="small"
                  [value]="filterClient()"
                  (synChangeEvent)="onClientChange($event)">
        <syn-option value="" i18n="@@select-all">(All)</syn-option>
        @for (entry of clients(); track entry) {
          <syn-option [value]="entry[0]">{{ entry[0] }} ({{ entry[1] }})</syn-option>
        }
      </syn-select>

      <syn-select label="Issues"
                  i18n-label="@@issues"
                  size="small"
                  [value]="filterReason()"
                  (synChangeEvent)="onReasonChange($event)">
        <syn-option value="" i18n="@@select-all">(All)</syn-option>
        @for (entry of reasons(); track entry) {
          <syn-option [value]="entry[0]">
            {{ entry[0] }} ({{ entry[1] }})
          </syn-option>
        }
      </syn-select>
    </div>

    <ng-template #rowTemplate let-value>
      <!-- Enable type checking in the template -->
      @let entry = identity(value);
      <tr>
        <td>
          <a [routerLink]="['/', 'clients', entry.subjectId, 'settings']">
            {{ entry.subjectName }}
          </a>
        </td>
        <td style="font-size: 24px;" [style.color]="color(entry.level)">
          <syn-icon [name]="icon(entry.level)" />
        </td>
        @let desc = description(entry);
        <td>
          @if (desc.link) {
            <a [href]="desc.link">
              {{ desc.name }}
            </a>
          } @else {
            {{ desc.name }}
          }
        </td>
        <td>{{ desc.text }}</td>
      </tr>
    </ng-template>
    <app-table
      [columns]="columns"
      [values]="filteredIssues()"
      [loading]="loading()"
      [clickable]="false"
      [rowTemplate]="rowTemplate"
      #table
    />
  `,
  styles: [
    `
      app-table {
        width: 100%;
      }
    `,
  ],
  imports: [TableComponent, SynIconComponent, SynSelectComponent, SynOptionComponent, RouterLink],
})
export class SecurityGuardComponent {
  api = inject(ServicePortalApi);
  state = inject(StateService);

  admin = signal(false);
  issues = signal<AuditIssue[]>([]);
  loading = signal(false);

  clients = signal<[string, number][]>([]);
  filterClient = signal<string>('');
  reasons = signal<[string, number][]>([]);
  filterReason = signal<string>('');

  filteredIssues = computed(() => {
    const all = this.issues();
    const filterClient = this.filterClient();
    const filterReason = this.filterReason();
    return all.filter((issue) => {
      if (filterClient !== '' && issue.subjectName !== filterClient) {
        return false;
      }
      if (filterReason !== '' && issue.reason !== filterReason) {
        return false;
      }
      return true;
    });
  });

  table = viewChild.required<TableComponent<AuditIssue>>('table');

  columns: ColumnDescription<AuditIssue>[] = [
    {
      label: $localize`:@@client-id:Client ID`,
      getField: (issue) => issue.subjectName,
      style: 'width: 15%',
    },
    {
      label: $localize`:@@level:Level`,
      getField: (issue) => issue.level,
      style: 'width: 5%',
    },
    {
      label: $localize`:@@issue:Issue`,
      getField: (issue) => issue.reason,
      style: 'width: 15%',
    },
    {
      label: $localize`:@@description:Description`,
      getField: (issue) => this.description(issue).text,
    },
  ];

  constructor() {
    effect(() => {
      const supportView = this.api.supportView();
      if (supportView) {
        console.log('Enabled support view');
      }
      this.filterClient.set('');
      this.filterReason.set('');
      this.load();
    });
  }

  load(): void {
    this.loading.set(true);
    this.api.getAudit(this.api.supportView(), (result) => {
      this.state.isAdmin.set(result.isServicePortalAdmin === true);

      this.issues.set(result.issues);
      this.loading.set(false);

      const clients = new Map<string, number>();
      const reasons = new Map<string, number>();
      for (const issue of result.issues) {
        const clientCount = clients.get(issue.subjectName) ?? 0;
        clients.set(issue.subjectName, clientCount + 1);

        const reasonCount = reasons.get(issue.reason) ?? 0;
        reasons.set(issue.reason, reasonCount + 1);
      }
      this.clients.set(toEntries(clients));
      this.reasons.set(toEntries(reasons));

      this.table().gotoFirstPage();
    });
  }

  identity(issue: AuditIssue) {
    return issue;
  }

  icon(level: string): string {
    switch (level) {
      case 'info':
        return 'info_fill';
      case 'warning':
        return 'warning_fill';
      case 'error':
        return 'error_fill';
      default:
        return 'info_fill';
    }
  }

  color(level: string): string {
    switch (level) {
      case 'info':
        return 'var(--syn-color-primary-500)';
      case 'warning':
        return 'var(--syn-color-warning-500)';
      case 'error':
        return 'var(--syn-color-error-500)';
      default:
        return 'black';
    }
  }

  description(issue: AuditIssue): IssueDescription {
    const desc = lookupDescription(issue.reason);
    if (!desc) {
      return { name: issue.reason, text: '' };
    }

    const result = { ...desc };
    result.text = result.text.replace('$data', issue.data ?? '(none)');
    return result;
  }

  onClientChange(event: SynChangeEvent) {
    if (!event.target) return;
    const select = event.target as SynSelect;
    if (typeof select.value === 'string') {
      this.filterClient.set(select.value);
      this.filterReason.set('');
      this.table().gotoFirstPage();
    }
  }

  onReasonChange(event: SynChangeEvent) {
    if (!event.target) return;
    const select = event.target as SynSelect;
    if (typeof select.value === 'string') {
      this.filterReason.set(select.value);
      this.filterClient.set('');
      this.table().gotoFirstPage();
    }
  }
}
